import React, { FC, KeyboardEventHandler, useCallback, useContext, useEffect, useState } from 'react';
import { Animation, ITransitionClasses } from '@src/core/Animation';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { classnames } from '@src/utils/classnames';
import styled from './ModalBackdrop.scss';
import { ModalContext } from '../context';
import { IDefaultComponentProps } from '@src/models/props';

interface IModalBackdropProps extends IDefaultComponentProps {
  open?: boolean;
  backdropTransitionClasses?: ITransitionClasses;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onBackdropClosed: () => void;
  onKeyUp: KeyboardEventHandler<HTMLDivElement>;
}

/** Задний фон диалога */
export const ModalBackdrop: FC<IModalBackdropProps> = ({
  children,
  backdropTransitionClasses,
  open,
  className,
  onClick,
  onBackdropClosed,
  onKeyUp,
  ...props
}) => {
  const { isMobile } = useContext(BreakpointsContext);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const { parentRef, setBackdropRef } = useContext(ModalContext);

  const handleSetRef = (e: HTMLDivElement | null) => {
    setBackdropRef(e);
    setRef(e);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === ref && isMouseDown) {
      onClick?.(e);
    }
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target && ref) {
        setIsMouseDown(e.target === ref);
      }
    },
    [ref],
  );

  useEffect(() => {
    ref?.focus();

    return () => {
      parentRef?.focus();
    };
  }, [parentRef, ref]);

  const classes = classnames(styled.root, className, {
    [styled.rootMobile]: isMobile,
  });

  return (
    <Animation open={open} onExited={onBackdropClosed} transitionClasses={backdropTransitionClasses}>
      <div
        {...props}
        ref={handleSetRef}
        role="presentation"
        className={classes}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onKeyUp={onKeyUp}
        tabIndex={-1}
      >
        {children}
      </div>
    </Animation>
  );
};
